<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <!-- Modern toolbar (all breakpoints) -->
      <div v-if="canShowFilters" class="mb-5 space-y-3">
        <div class="list-toolbar">
          <BaseButton
            v-if="canViewDonationMap"
            variant="secondary"
            :icon="MapIcon"
            :loading="showMapView ? mapUpdating : mapLoading"
            @click="toggleMapView"
          >
            {{ showMapView ? 'View Donations' : 'Donation Locations' }}
          </BaseButton>
          <BaseButton
            v-if="canExport"
            variant="secondary"
            :icon="Cog6ToothIcon"
            @click="showCustomizeModal = true"
          >
            Customize Export
          </BaseButton>
          <BaseButton
            v-if="canExport"
            variant="secondary"
            :icon="DocumentDuplicateIcon"
            @click="showTemplatesModal = true"
          >
            Templates
          </BaseButton>
          <BaseButton
            v-if="canExport"
            variant="primary"
            :icon="ArrowDownTrayIcon"
            @click="exportDonations"
          >
            Export
          </BaseButton>
          <div class="list-toolbar__right">
            <span class="list-toolbar-badge">
              <span class="text-gray-400">Records</span>
              <span class="font-semibold text-gray-900">{{ donations.pagination.totalCount }}</span>
            </span>
            <BaseButton
              v-if="activeFilterCount"
              variant="ghost"
              :icon="ArrowPathIcon"
              @click="clearSorting"
            >
              Clear All
            </BaseButton>
            <BaseButton
              variant="secondary"
              :icon="FunnelIcon"
              @click="showFilterDrawer = !showFilterDrawer"
            >
              Filters
              <span
                v-if="activeFilterCount"
                class="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
              >
                {{ activeFilterCount }}
              </span>
            </BaseButton>
          </div>
        </div>

        <!-- Mobile sort chips -->
        <div class="flex flex-wrap gap-2 sm:hidden">
          <button
            v-for="column in sortColumnOptions"
            :key="column.value"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
            @click="sortData(column.value)"
          >
            {{ column.name }}
            <FontAwesomeIcon
              :icon="getSortIcon(column.value)"
              :class="getSortOrder(column.value) ? 'text-primary' : 'text-gray-300'"
            />
          </button>
        </div>
      </div>

      <!-- Inline filters -->
      <FilterDrawer
        :open="showFilterDrawer"
        title="Filter Donations"
        subtitle="Refine the donation list"
        @close="showFilterDrawer = false"
      >
        <div class="filter-fields">
          <div class="filter-field">
            <label class="filter-field-label">Date range</label>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FlatPickr
                id="start"
                name="start"
                class="input--text--small placeholder:text-gray-400 w-full"
                placeholder="Start date"
                :value="selectedDateRange.date1"
                @input="handleDateChange('date1', $event.target.value)"
              />
              <FlatPickr
                id="end"
                name="end"
                class="input--text--small placeholder:text-gray-400 w-full"
                placeholder="End date"
                :value="selectedDateRange.date2"
                @input="handleDateChange('date2', $event.target.value)"
              />
            </div>
          </div>

          <FilterMultiselect
            field-label="Campaigns"
            v-model="selectedCampaignFilter"
            :options="campaignFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select campaigns"
            :custom-label="customLabel"
            selectLabel="Apply"
            deselectLabel="Remove"
            track-by="id"
          />

          <FilterMultiselect
            field-label="Fundraisers"
            v-model="selectedFundraiserFilter"
            :options="fundraiserFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Search fundraisers..."
            selectLabel="Apply"
            deselectLabel="Remove"
            label="full_name"
            track-by="id"
            :loading="isSearchingFundraisers"
            :searchable="true"
            :internal-search="false"
            @search-change="handleFundraiserSearch"
          >
            <template #noResult>
              <span class="text-sm text-gray-500"
                >No fundraisers found. Try a different search.</span
              >
            </template>
            <template #noOptions>
              <span class="text-sm text-gray-500">Type to search for fundraisers</span>
            </template>
          </FilterMultiselect>

          <FilterMultiselect
            field-label="Frequencies"
            v-model="selectedFrequencyFilter"
            :options="frequencyFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select frequencies"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="name"
            track-by="name"
          />

          <FilterMultiselect
            field-label="Transaction Outcome"
            v-model="selectedStatusFilter"
            :options="statusFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select transaction outcomes"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="name"
            track-by="name"
          />

          <FilterMultiselect
            field-label="Current Donor Status"
            v-model="selectedDonorStatusFilter"
            :options="donorStatusFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select donor statuses"
            selectLabel="Apply"
            deselectLabel="Remove"
            label="name"
            track-by="id"
          />

          <FilterMultiselect
            field-label="Donors"
            v-model="selectedDonorFilter"
            :options="donorFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Search donors..."
            selectLabel="Apply"
            deselectLabel="Remove"
            :custom-label="donorCustomLabel"
            track-by="id"
            :loading="isSearchingDonors"
            :searchable="true"
            :internal-search="false"
            @search-change="handleDonorSearch"
          >
            <template #noResult>
              <span class="text-sm text-gray-500">No donors found. Try a different search.</span>
            </template>
            <template #noOptions>
              <span class="text-sm text-gray-500">Type to search for donors</span>
            </template>
          </FilterMultiselect>

          <FilterMultiselect
            field-label="Givaroo ID"
            v-model="selectedGivarooIdFilter"
            :options="givarooIdFilters"
            :multiple="true"
            :close-on-select="false"
            placeholder="Search Givaroo ID..."
            selectLabel="Apply"
            deselectLabel="Remove"
            :loading="isSearchingGivarooIds"
            :searchable="true"
            :internal-search="false"
            @search-change="handleGivarooIdSearch"
            label="givaroo_id"
            track-by="givaroo_id"
          >
            <template #noResult>
              <span class="text-sm text-gray-500"
                >No Givaroo IDs found. Try a different search.</span
              >
            </template>
            <template #noOptions>
              <span class="text-sm text-gray-500">Type to search for Givaroo IDs</span>
            </template>
          </FilterMultiselect>
        </div>

        <template #footer>
          <div class="filter-drawer-footer">
            <BaseButton
              v-if="activeFilterCount"
              variant="ghost"
              :icon="ArrowPathIcon"
              @click="clearSorting"
            >
              Clear all
            </BaseButton>
            <BaseButton variant="primary" @click="showFilterDrawer = false">Done</BaseButton>
          </div>
        </template>
      </FilterDrawer>

      <!-- Donation list (smallest breakpoint only) -->
      <div class="sm:hidden">
        <template v-if="!showMapView">
          <Spinner v-if="updatingTable" class="mt-8" />
          <template v-else>
            <!-- Donation list for mobile -->
            <ul v-if="donations.list?.length" role="list" class="mt-2 space-y-3 sm:hidden">
              <li
                v-for="(donation, key) in donations.list"
                :key="key"
                class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-gray-900">
                      {{ donation.donorRef.firstName }} {{ donation.donorRef.lastName }}
                    </p>
                    <p class="truncate text-xs text-gray-500">
                      {{
                        donation.campaign.location
                          ? donation.campaign.name + ' ' + donation.campaign.location
                          : donation.campaign.name
                      }}
                    </p>
                    <p class="mt-1 text-sm">
                      <span class="font-semibold text-gray-900">{{
                        formatCurrency(donation.amount)
                      }}</span>
                      <span class="text-gray-400"> &middot; </span>
                      <span class="text-gray-600">{{
                        formatToTitleCase(donation.frequency.value)
                      }}</span>
                    </p>
                  </div>
                  <div class="flex shrink-0 flex-col items-end gap-1 text-right">
                    <span class="text-xs text-gray-500">{{
                      formatDateString(donation.datetimeCreated, donation.campaign?.timezone)
                    }}</span>
                    <span class="text-xs text-gray-400">ID: {{ donation.givarooId }}</span>
                    <button
                      v-if="isFundraiser"
                      type="button"
                      :class="notesButtonClass"
                      @click="openNotesModal(donation)"
                    >
                      {{ donation.notes ? 'Edit note' : 'Add note' }}
                    </button>
                    <BaseButton
                      v-if="donation.canResubmit"
                      variant="subtle"
                      size="xs"
                      :loading="resubmittingDonationId === donation.id"
                      @click="resubmitDonation(donation)"
                    >
                      {{ resubmittingDonationId === donation.id ? 'Resubmitting…' : 'Resubmit' }}
                    </BaseButton>
                    <span v-else :class="statusBadgeClass(donation.status)">
                      {{ getStatus(donation.status) }}
                    </span>
                  </div>
                </div>
              </li>
            </ul>
            <p
              v-else
              class="mt-2 rounded-xl bg-white p-6 text-center text-sm text-gray-500 ring-1 ring-gray-200"
            >
              No results found.
            </p>
          </template>
        </template>
      </div>

      <!-- Donation list (small breakpoint and up) -->
      <div v-if="!showMapView" class="hidden sm:block">
        <BaseTable
          :columns="tableColumns"
          :rows="donations.list"
          :loading="updatingTable"
          :get-sort-icon="getSortIcon"
          :get-sort-order="getSortOrder"
          @sort="sortData"
        >
          <template #cell-donor="{ row }">
            <span class="font-medium text-gray-900">
              {{ row.donorRef.firstName }} {{ row.donorRef.lastName }}
            </span>
          </template>
          <template #cell-date="{ row }">{{
            formatDateString(row.datetimeCreated, row.campaign?.timezone)
          }}</template>
          <template #cell-campaign="{ row }">
            {{
              row.campaign.location
                ? row.campaign.name + ' ' + row.campaign.location
                : row.campaign.name
            }}
          </template>
          <template #cell-fundraiser="{ row }">
            {{ row.fundraiser ? row.fundraiser.fullName : 'N/A' }}
          </template>
          <template #cell-amount="{ row }">
            <span class="font-medium text-gray-900">{{ formatCurrency(row.amount) }}</span>
          </template>
          <template #cell-frequency="{ row }">
            {{ formatToTitleCase(row.frequency.value) }}
          </template>
          <template #cell-status="{ row }">
            <BaseButton
              v-if="row.canResubmit"
              variant="subtle"
              size="xs"
              :loading="resubmittingDonationId === row.id"
              @click="resubmitDonation(row)"
            >
              {{ resubmittingDonationId === row.id ? 'Resubmitting…' : 'Resubmit' }}
            </BaseButton>
            <span v-else :class="statusBadgeClass(row.status)">{{ getStatus(row.status) }}</span>
          </template>
          <template #cell-id="{ row }">{{ row.givarooId }}</template>
          <template #cell-notes="{ row }">
            <button type="button" :class="notesButtonClass" @click="openNotesModal(row)">
              {{ row.notes ? 'Edit note' : 'Add note' }}
            </button>
          </template>
        </BaseTable>
      </div>

      <Modal
        headerText="Donation notes"
        subheaderText="Add a note for this donation."
        ctaText="Save"
        :modalOpen="showNotesModal"
        :showCancelButton="true"
        :disableCTA="savingNotes"
        @cancelAction="closeNotesModal"
        @closeModal="saveNotes"
      >
        <textarea
          v-model="notesDraft"
          rows="5"
          class="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
          placeholder="Type your note here…"
        />
      </Modal>

      <DonationMapSection
        v-if="canViewDonationMap"
        :active="showMapView"
        :locations="mapLocations"
        :loading="mapLoading"
        :can-draw-export-area="canExport"
        @polygon-change="onMapPolygonChange"
      />

      <!-- Customize Export Template Sidebar -->
      <div
        v-if="showCustomizeModal"
        class="fixed inset-0 overflow-hidden z-50"
        aria-labelledby="slide-over-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="showCustomizeModal = false"
            aria-hidden="true"
          ></div>
          <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div class="w-screen max-w-md">
              <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
                  <div class="flex items-start justify-between">
                    <h2 class="text-lg font-medium text-gray-900" id="slide-over-title">
                      Customize Export Template
                    </h2>
                    <div class="ml-3 h-7 flex items-center">
                      <button
                        type="button"
                        class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                        @click="showCustomizeModal = false"
                      >
                        <span class="sr-only">Close panel</span>
                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div class="mt-8">
                    <div class="flow-root">
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-for="(field, key) in exportFields"
                          :key="key"
                          @click="toggleExportField(key)"
                          class="cursor-pointer"
                        >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors"
                            :class="{
                              'bg-primary text-white': field.selected,
                              'bg-gray-100 text-gray-700 hover:bg-gray-200': !field.selected,
                            }"
                          >
                            {{ field.label }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Date range inputs and filters -->
                  <div class="flex flex-wrap gap-4 mt-8">
                    <div class="flex items-center gap-2 flex-grow">
                      <FlatPickr
                        id="start"
                        name="start"
                        class="input--text--small placeholder:text-gray-400 date-input flex-grow"
                        placeholder="Select date start"
                        :value="selectedDateRange.date1"
                        @input="handleDateChange('date1', $event.target.value)"
                      />
                    </div>
                    <div class="flex items-center gap-2 flex-grow">
                      <FlatPickr
                        id="end"
                        name="end"
                        class="input--text--small placeholder:text-gray-400 date-input flex-grow"
                        placeholder="Select date end"
                        :value="selectedDateRange.date2"
                        @input="handleDateChange('date2', $event.target.value)"
                      />
                    </div>
                  </div>
                </div>

                <div class="border-t border-gray-200 py-6 px-4 sm:px-6 flex justify-between">
                  <div class="flex gap-2">
                    <BaseButton variant="ghost" @click="clearExportSelection">
                      Clear Selection
                    </BaseButton>
                    <BaseButton
                      variant="secondary"
                      :disabled="selectedFieldsOrder.length === 0"
                      @click="openSaveTemplateModal"
                    >
                      Save Template
                    </BaseButton>
                  </div>
                  <BaseButton
                    variant="primary"
                    :icon="ArrowDownTrayIcon"
                    @click="exportCustomTemplate"
                  >
                    Export
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Template Modal -->
      <div
        v-if="showSaveTemplateModal"
        class="fixed inset-0 overflow-hidden z-50"
        aria-labelledby="save-template-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="showSaveTemplateModal = false"
            aria-hidden="true"
          ></div>
          <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div class="w-screen max-w-md">
              <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
                  <div class="flex items-start justify-between">
                    <h2 class="text-lg font-medium text-gray-900" id="save-template-title">
                      Save Export Template
                    </h2>
                    <div class="ml-3 h-7 flex items-center">
                      <button
                        type="button"
                        class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                        @click="showSaveTemplateModal = false"
                      >
                        <span class="sr-only">Close panel</span>
                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div class="mt-8 space-y-4">
                    <div>
                      <label for="template-name" class="block text-sm font-medium text-gray-700">
                        Template Name
                      </label>
                      <input
                        type="text"
                        id="template-name"
                        v-model="templateName"
                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter template name"
                      />
                    </div>

                    <div class="flex items-center justify-between gap-3">
                      <div>
                        <p class="text-sm font-medium text-gray-700">Automatic export</p>
                        <p class="text-xs text-gray-500">
                          Email a CSV attachment on a regular schedule
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        :aria-checked="autoExportEnabled"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        :class="autoExportEnabled ? 'bg-primary' : 'bg-gray-200'"
                        @click="autoExportEnabled = !autoExportEnabled"
                      >
                        <span
                          class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                          :class="autoExportEnabled ? 'translate-x-5' : 'translate-x-0'"
                        />
                      </button>
                    </div>

                    <template v-if="autoExportEnabled">
                      <div>
                        <label
                          for="schedule-frequency"
                          class="block text-sm font-medium text-gray-700"
                        >
                          Frequency
                        </label>
                        <select
                          id="schedule-frequency"
                          v-model="scheduleFrequency"
                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>

                      <div v-if="scheduleFrequency === 'weekly'">
                        <label
                          for="schedule-weekday"
                          class="block text-sm font-medium text-gray-700"
                        >
                          Pull day
                        </label>
                        <select
                          id="schedule-weekday"
                          v-model.number="scheduleWeekday"
                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option v-for="day in weekdayOptions" :key="day.value" :value="day.value">
                            {{ day.label }}
                          </option>
                        </select>
                      </div>

                      <div>
                        <label
                          for="schedule-start-date"
                          class="block text-sm font-medium text-gray-700"
                        >
                          Start date
                        </label>
                        <FlatPickr
                          id="schedule-start-date"
                          name="schedule-start-date"
                          class="mt-1 input--text--small placeholder:text-gray-400 date-input w-full"
                          placeholder="Select start date"
                          :config="scheduleStartDateConfig"
                          :value="scheduleStartDate"
                          @input="scheduleStartDate = $event.target.value"
                        />
                      </div>
                    </template>
                  </div>
                </div>

                <div class="border-t border-gray-200 py-6 px-4 sm:px-6 flex justify-between">
                  <BaseButton variant="secondary" @click="showSaveTemplateModal = false">
                    Cancel
                  </BaseButton>
                  <BaseButton
                    variant="primary"
                    :disabled="!canSaveExportTemplate"
                    @click="saveTemplate"
                  >
                    Save
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showTemplatesModal"
        class="fixed inset-0 overflow-hidden z-50"
        aria-labelledby="templates-modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="showTemplatesModal = false"
            aria-hidden="true"
          ></div>
          <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div class="w-screen max-w-md">
              <div class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                <div class="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
                  <div class="flex items-start justify-between">
                    <h2 class="text-lg font-medium text-gray-900" id="templates-modal-title">
                      Saved Templates
                    </h2>
                    <div class="ml-3 h-7 flex items-center">
                      <button
                        type="button"
                        class="-m-2 p-2 text-gray-400 hover:text-gray-500"
                        @click="showTemplatesModal = false"
                      >
                        <span class="sr-only">Close panel</span>
                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div class="mt-8">
                    <div v-if="loadingTemplates" class="text-center">
                      <Spinner />
                    </div>
                    <ul v-else class="divide-y divide-gray-200">
                      <li
                        v-for="template in savedTemplates"
                        :key="template.id"
                        class="py-4 flex justify-between items-center"
                      >
                        <div class="flex-grow min-w-0 pr-2">
                          <span class="text-sm font-medium text-gray-900 block truncate">{{
                            template.name
                          }}</span>
                          <span
                            v-if="getTemplateScheduleLabel(template)"
                            class="text-xs text-gray-500"
                          >
                            {{ getTemplateScheduleLabel(template) }}
                          </span>
                        </div>
                        <div class="flex gap-2 flex-shrink-0">
                          <BaseButton variant="secondary" @click="applyTemplate(template)">
                            Apply
                          </BaseButton>
                          <BaseButton variant="danger" @click="removeTemplate(template.id)">
                            Remove
                          </BaseButton>
                        </div>
                      </li>
                      <li v-if="!savedTemplates.length" class="py-4 text-center text-gray-500">
                        No saved templates found
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="border-t border-gray-200 py-6 px-4 sm:px-6">
                  <BaseButton variant="secondary" block @click="showTemplatesModal = false">
                    Close
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <BasePagination
        v-if="!showMapView && donations.list?.length"
        v-model="itemsPerPage"
        :page="donations.pagination.page"
        :total-pages="totalPages"
        :total-count="donations.pagination.totalCount"
        :display-start="displayStart"
        :display-end="displayEnd"
        :has-prev-page="donations.pagination.hasPrevPage"
        :has-next-page="donations.pagination.hasNextPage"
        @page-size-change="updateItemsPerPage"
        @prev="prevPage"
        @next="nextPage"
      />
    </template>
  </DashboardContainer>
</template>

<script>
import { computed, onBeforeMount, reactive, ref, triggerRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import DashboardContainer from '@/components/DashboardContainer.vue'
import Spinner from '@/components/Spinner'
import { donationFunctions, DonationApi } from '@/services/donations/'
import options from '@/services/donations/options/'
import {
  CAMPAIGN_DATA_FUNDRAISER_FILTER_TYPE,
  FUNDRAISER_STATS_BY_DAY_FILTER_TYPE,
} from '@/services/workingHours/models'
import {
  XMarkIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  MapIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
} from '@heroicons/vue/24/outline'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { useToast } from 'vue-toastification'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import DonationMapSection from '@/components/DonationMapSection.vue'
import Modal from '@/components/Modal'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseTable from '@/components/ui/BaseTable.vue'
import FilterDrawer from '@/components/ui/FilterDrawer.vue'
import FilterMultiselect from '@/components/ui/FilterMultiselect.vue'
import BasePagination from '@/components/ui/BasePagination.vue'
import { formatInTimezone } from '@/utils/dateHelpers'

// In setup()

library.add(faSort, faSortUp, faSortDown)

export default {
  name: 'DonationList',
  components: {
    DashboardContainer,
    Spinner,
    XMarkIcon,
    FontAwesomeIcon,
    FlatPickr,
    GivarooSpinner,
    DonationMapSection,
    Modal,
    BaseButton,
    BaseTable,
    FilterDrawer,
    FilterMultiselect,
    BasePagination,
  },
  setup() {
    const router = useRouter()
    const store = useStore()
    const userId = store.getters.user?.id
    const isGivarooStaff = ref(store.getters.isGivarooStaff)
    const isNPOAdmin = ref(store.getters.isNPOAdmin)
    const isCanvassingOrgAdmin = ref(store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = ref(store.getters.isCanvassingOrgFieldDirector)
    const isFundraiser = computed(() => store.getters.isFundraiser)

    const { donationCollection, donationFilters } = donationFunctions()
    const donations = ref(donationCollection)
    const donationRefs = ref([])
    const donationStatuses = reactive(options.DONATION_STATUS_CHOICES)

    const loading = ref(false)
    const updatingTable = ref(false)
    const itemsPerPage = ref(50)
    const totalPages = ref()
    const toast = useToast()
    const resubmittingDonationId = ref(null)
    const showNotesModal = ref(false)
    const notesDraft = ref('')
    const notesDonation = ref(null)
    const savingNotes = ref(false)
    const mapLocations = ref([])
    const mapLoading = ref(false)
    const mapLocationsStale = ref(true)
    const exportPolygon = ref(null)

    const filterIndexes = reactive({
      campaigns: [],
      fundraisers: [],
      frequencies: [],
      statuses: [],
      donorStatuses: [],
      donors: [],
      givarooIds: [],
    })

    const campaignFilters = computed(() => filterIndexes.campaigns)
    const fundraiserFilters = computed(() => filterIndexes.fundraisers)
    const frequencyFilters = computed(() => filterIndexes.frequencies)
    const statusFilters = computed(() => filterIndexes.statuses)
    const donorStatusFilters = computed(() => filterIndexes.donorStatuses)
    const donorFilters = computed(() => filterIndexes.donors)
    const givarooIdFilters = computed(() => filterIndexes.givarooIds)
    const canExport = computed(() => {
      return isGivarooStaff.value || isNPOAdmin.value || isCanvassingOrgAdmin.value
    })
    const canViewDonationMap = computed(
      () => isCanvassingOrgAdmin.value || isCanvassingOrgFieldDirector.value,
    )

    const selectedCampaignFilter = ref([])
    const selectedFundraiserFilter = ref([])
    const selectedFrequencyFilter = ref([])
    const selectedStatusFilter = ref([])
    const selectedDonorStatusFilter = ref([])
    const selectedDateRange = reactive({
      date1: '',
      date2: '',
    })
    const selectedDonorFilter = ref([])
    const selectedGivarooIdFilter = ref([])
    const givarooIdSearchQuery = ref('')
    const isSearchingGivarooIds = ref(false)

    // Modern UI: filter drawer state + helpers (presentation only)
    const showFilterDrawer = ref(false)
    const canShowFilters = computed(
      () =>
        isGivarooStaff.value ||
        isNPOAdmin.value ||
        isCanvassingOrgAdmin.value ||
        isCanvassingOrgFieldDirector.value,
    )
    const activeFilterCount = computed(() => {
      let count = 0
      count += selectedCampaignFilter.value.length
      count += selectedFundraiserFilter.value.length
      count += selectedFrequencyFilter.value.length
      count += selectedStatusFilter.value.length
      count += selectedDonorStatusFilter.value.length
      count += selectedDonorFilter.value.length
      count += selectedGivarooIdFilter.value.length
      if (selectedDateRange.date1 && selectedDateRange.date2) count += 1
      return count
    })

    const tableColumns = computed(() => {
      const columns = [
        { key: 'donor', label: 'Donor', sortable: true, minWidth: '10rem' },
        { key: 'date', label: 'Date', sortable: true, minWidth: '8rem' },
        { key: 'campaign', label: 'Campaign', sortable: true, minWidth: '10rem' },
        { key: 'fundraiser', label: 'Fundraiser', sortable: true, minWidth: '9rem' },
        { key: 'amount', label: 'Amount', sortable: true, minWidth: '7rem' },
        { key: 'frequency', label: 'Frequency', sortable: true, minWidth: '8rem' },
        { key: 'status', label: 'Status', sortable: true, minWidth: '9rem' },
        { key: 'id', label: 'ID', sortable: true, minWidth: '7rem' },
      ]
      if (isFundraiser.value) {
        columns.push({ key: 'notes', label: 'Notes', sortable: false, minWidth: '9rem' })
      }
      return columns
    })

    const notesButtonClass =
      'inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primaryAccent hover:bg-primary/20'

    const statusBadgeClass = (status) => {
      const base = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium'
      const styles = {
        [options.SUCCESS]: 'bg-green-100 text-green-700',
        [options.FAILURE]: 'bg-red-100 text-red-700',
        [options.PENDING]: 'bg-amber-100 text-amber-700',
      }
      return `${base} ${styles[status] || 'bg-gray-100 text-gray-600'}`
    }

    const appliedFilters = ref([])
    const sortColumnOptions = [
      { name: 'Donor', value: 'donor' },
      { name: 'Date', value: 'date' },
      { name: 'Campaign', value: 'campaign' },
      { name: 'Fundraiser', value: 'fundraiser' },
      { name: 'Amount', value: 'amount' },
      { name: 'Frequency', value: 'frequency' },
      { name: 'Status', value: 'status' },
      { name: 'ID', value: 'id' },
    ]

    // Add these new refs to your setup() function
    const showTemplatesModal = ref(false)
    const showMapView = ref(false)
    const mapUpdating = computed(
      () => mapLoading.value || (showMapView.value && updatingTable.value),
    )
    const savedTemplates = ref([])
    const loadingTemplates = ref(false)

    // In your setup() function
    const donorSearchQuery = ref('')
    const isSearchingDonors = ref(false)

    // Add a debounce function
    const debounce = (fn, delay) => {
      let timeoutId
      return function (...args) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn.apply(this, args), delay)
      }
    }

    // Search donors function
    const searchDonors = debounce(async (query) => {
      if (!query || query.length < 2) {
        filterIndexes.donors = []
        return
      }

      isSearchingDonors.value = true
      try {
        const filters = {
          query,
          fundraiser: selectedFundraiserFilter.value.map((f) => f.id).join(','),
          status: selectedStatusFilter.value.map((s) => s.id).join(','),
          // Send frequency IDs instead of names
          frequency: selectedFrequencyFilter.value.map((f) => f.id).join(','),
          campaign: selectedCampaignFilter.value.map((c) => c.id).join(','),
        }

        const response = await DonationApi.csc.searchDonors(filters)
        filterIndexes.donors = response
      } catch (error) {
        console.error('Error searching donors:', error)
      } finally {
        isSearchingDonors.value = false
      }
    }, 300)

    // Watch for changes in search query
    watch(donorSearchQuery, (newQuery) => {
      searchDonors(newQuery)
    })
    const handleDonorSearch = (query) => {
      donorSearchQuery.value = query
    }

    // Add these refs
    const fundraiserSearchQuery = ref('')
    const isSearchingFundraisers = ref(false)

    // Add debounced search function
    const searchFundraisers = debounce(async (query) => {
      if (!query || query.length < 2) {
        filterIndexes.fundraisers = []
        return
      }

      isSearchingFundraisers.value = true
      try {
        const response = await DonationApi.csc.searchFundraisers({ query })
        filterIndexes.fundraisers = response
      } catch (error) {
        console.error('Error searching fundraisers:', error)
      } finally {
        isSearchingFundraisers.value = false
      }
    }, 300)

    // Watch for changes in search query
    watch(fundraiserSearchQuery, (newQuery) => {
      searchFundraisers(newQuery)
    })

    const handleFundraiserSearch = (query) => {
      fundraiserSearchQuery.value = query
    }
    // Givaroo ID Search Function
    const searchGivarooIds = debounce(async (query) => {
      if (!query || query.length < 1) {
        filterIndexes.givarooIds = []
        return
      }

      isSearchingGivarooIds.value = true
      try {
        const response = await DonationApi.csc.searchGivarooIds({ query })
        filterIndexes.givarooIds = response
      } catch (error) {
        console.error('Error searching Givaroo IDs:', error)
      } finally {
        isSearchingGivarooIds.value = false
      }
    }, 300)

    // Add watcher
    watch(givarooIdSearchQuery, (newQuery) => {
      searchGivarooIds(newQuery)
    })

    // Add handler
    const handleGivarooIdSearch = (query) => {
      givarooIdSearchQuery.value = query
    }

    const getSortOrder = (column) => {
      const filter = appliedFilters.value.find((filter) => filter.column === column)
      return filter ? filter.order : null
    }

    const sortData = (column) => {
      const existingFilterIndex = appliedFilters.value.findIndex(
        (filter) => filter.column === column,
      )

      if (existingFilterIndex !== -1) {
        appliedFilters.value[existingFilterIndex].order =
          appliedFilters.value[existingFilterIndex].order === 'asc' ? 'desc' : 'asc'
      } else {
        appliedFilters.value.push({ column, order: 'asc' })
      }

      donations.value.list.sort((a, b) => {
        for (const filter of appliedFilters.value) {
          let valueA, valueB

          switch (filter.column) {
            case 'donor':
              valueA = `${a.donorRef.firstName} ${a.donorRef.lastName}`.toUpperCase()
              valueB = `${b.donorRef.firstName} ${b.donorRef.lastName}`.toUpperCase()
              break
            case 'date':
              valueA = new Date(a.datetimeCreated)
              valueB = new Date(b.datetimeCreated)
              break
            case 'campaign':
              valueA = a.campaign.name.toUpperCase()
              valueB = b.campaign.name.toUpperCase()
              break
            case 'fundraiser':
              valueA = a.fundraiser ? a.fundraiser.fullName.toUpperCase() : 'N/A'
              valueB = b.fundraiser ? b.fundraiser.fullName.toUpperCase() : 'N/A'
              break
            case 'amount':
              valueA = a.amount
              valueB = b.amount
              break
            case 'frequency':
              valueA = a.frequency.value.toUpperCase()
              valueB = b.frequency.value.toUpperCase()
              break
            case 'status':
              valueA = a.status.toUpperCase()
              valueB = b.status.toUpperCase()
              break
            case 'id':
              valueA = a.givarooId
              valueB = b.givarooId
              break
            default:
              return 0
          }

          if (valueA < valueB) return filter.order === 'asc' ? -1 : 1
          if (valueA > valueB) return filter.order === 'asc' ? 1 : -1
        }

        return 0
      })
    }
    const displayStart = computed(() => {
      return (donations.value.pagination.page - 1) * itemsPerPage.value + 1
    })

    const displayEnd = computed(() => {
      const end = donations.value.pagination.page * itemsPerPage.value
      return end > donations.value.pagination.totalCount
        ? donations.value.pagination.totalCount
        : end
    })

    const clearSorting = () => {
      window.location.reload()
    }

    const updateItemsPerPage = async () => {
      updatingTable.value = true
      donations.value.pagination.size = itemsPerPage.value
      donations.value.pagination.page = 1
      await donations.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(donations)
      totalPages.value = Math.ceil(donations.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
    }

    const nextPage = async () => {
      if (donations.value.pagination.hasNextPage) {
        updatingTable.value = true
        donations.value.pagination.size = itemsPerPage.value
        await donations.value.nextPage().catch((error) => {
          alert(error)
        })
        triggerRef(donations)
        totalPages.value = Math.ceil(donations.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }

    const prevPage = async () => {
      if (donations.value.pagination.hasPrevPage) {
        updatingTable.value = true
        donations.value.pagination.size = itemsPerPage.value
        await donations.value.prevPage().catch((error) => {
          alert(error)
        })
        triggerRef(donations)
        totalPages.value = Math.ceil(donations.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }

    onBeforeMount(async () => {
      loading.value = true

      donations.value.pagination.size = itemsPerPage.value
      await donations.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(donations)
      totalPages.value = Math.ceil(donations.value.pagination.totalCount / itemsPerPage.value)

      const response = await DonationApi.csc.retrieveFilterIndexes()

      filterIndexes.campaigns = response.campaigns || []
      filterIndexes.frequencies = response.frequencies || []
      filterIndexes.statuses = response.statuses || []
      filterIndexes.donorStatuses = response.donorStatuses || []

      loading.value = false
    })

    const setFilters = () => {
      donationFilters.campaign = selectedCampaignFilter.value
        .map((campaign) => campaign.id)
        .join(',')
      donationFilters.fundraiser = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      donationFilters.frequency = selectedFrequencyFilter.value
        .map((frequency) => frequency.id)
        .join(',')
      donationFilters.donor = selectedDonorFilter.value.map((donor) => donor.id).join(',')
      donationFilters.status = selectedStatusFilter.value.map((status) => status.id).join(',')
      donationFilters.currentDonorStatus = selectedDonorStatusFilter.value
        .map((status) => status.id)
        .join(',')
      donationFilters.givarooId = selectedGivarooIdFilter.value
        .map((item) => item.givaroo_id)
        .join(',')
      getStartAndEndDates()
    }
    const customLabel = (option) => {
      return `${option.name} - ${option.location}`
    }
    const donorCustomLabel = (donor) => {
      return `${donor.first_name} ${donor.last_name}`
    }

    const onMapPolygonChange = (coordinates) => {
      exportPolygon.value = coordinates?.length >= 3 ? coordinates : null
    }

    const loadMapLocations = async () => {
      if (!canViewDonationMap.value) {
        return
      }
      mapLoading.value = true
      try {
        setFilters()
        const { ordering: _ordering, ...filters } = donationFilters
        mapLocations.value = await DonationApi.csc.retrieveMapLocations({ ...filters })
        mapLocationsStale.value = false
      } catch (error) {
        console.error('Failed to load donation map locations:', error)
        mapLocations.value = []
      } finally {
        mapLoading.value = false
      }
    }

    const toggleMapView = async () => {
      if (showMapView.value) {
        showMapView.value = false
        return
      }
      showMapView.value = true
      if (mapLocationsStale.value) {
        await loadMapLocations()
      }
    }

    const getDonations = async () => {
      updatingTable.value = true
      setFilters()
      donations.value.pagination.page = 1
      donations.value.pagination.size = itemsPerPage.value
      await donations.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(donations)
      totalPages.value = Math.ceil(donations.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
      if (showMapView.value) {
        await loadMapLocations()
      } else if (canViewDonationMap.value) {
        mapLocationsStale.value = true
      }
    }

    const formatDateString = (dateString, timezone) => {
      return formatInTimezone(dateString, timezone)
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount)
    }

    const formatToTitleCase = (value) => {
      return value.replace(/(?<=\b)\w/g, (word) => word.toUpperCase())
    }

    const getStatus = (donationStatus) => {
      return donationStatuses.find((status) => status.id == donationStatus).name
    }

    const formatResubmitMessage = (message) => {
      if (!message) return ''
      if (typeof message === 'string') return message
      if (message?.error) return String(message.error)
      return JSON.stringify(message)
    }

    const openNotesModal = (donation) => {
      notesDonation.value = donation
      notesDraft.value = donation.notes || ''
      showNotesModal.value = true
    }

    const closeNotesModal = () => {
      showNotesModal.value = false
      notesDonation.value = null
      notesDraft.value = ''
    }

    const saveNotes = async () => {
      if (!notesDonation.value || savingNotes.value) return
      savingNotes.value = true
      try {
        const result = await DonationApi.csc.updateDonationNotes({
          donationId: notesDonation.value.id,
          notes: notesDraft.value,
        })
        notesDonation.value.notes = result.notes
        toast.success('Note saved.')
        closeNotesModal()
      } catch (error) {
        toast.error(
          error.response?.data?.detail ||
            error.response?.data?.notes?.[0] ||
            'Failed to save note.',
        )
      } finally {
        savingNotes.value = false
      }
    }

    const resubmitDonation = async (donation) => {
      if (resubmittingDonationId.value) return
      resubmittingDonationId.value = donation.id
      try {
        const result = await DonationApi.csc.resubmitDonation({ donationId: donation.id })
        const status = result.donationStatus
        const msg = formatResubmitMessage(result.message)

        if (status === 'SUCCESS') {
          toast.success(msg || 'Donation processed successfully.')
        } else if (status === 'PENDING') {
          toast.warning(
            result.transactionErrorMsg ||
              msg ||
              'Resubmission failed. Please try again later after connection has been reestablished.',
          )
        } else {
          toast.error(result.transactionErrorMsg || msg || 'Donation failed.')
        }
        await getDonations()
      } catch (error) {
        const status = error?.response?.status
        const detail =
          error?.response?.data?.detail ||
          error?.response?.data?.[0] ||
          error?.message ||
          'Resubmit failed.'
        if (status === 429) {
          toast.warning(detail)
        } else {
          toast.error(detail)
        }
        await getDonations()
      } finally {
        resubmittingDonationId.value = null
      }
    }
    const filterByDate = () => {
      if (selectedDateRange.date1 && selectedDateRange.date2) {
        getDonations()
      }
    }

    const handleDateChange = (dateField, value) => {
      selectedDateRange[dateField] = value
      filterByDate()
    }

    const getStartAndEndDates = () => {
      let date1 = selectedDateRange.date1
      let date2 = selectedDateRange.date2
      if (date1 && date2) {
        if (date1 >= date2) {
          donationFilters.dateRangeStart = date2
          donationFilters.dateRangeEnd = date1
        } else if (date1 <= date2) {
          donationFilters.dateRangeStart = date1
          donationFilters.dateRangeEnd = date2
        }
      } else if (!date1 || !date2) {
        donationFilters.dateRangeStart = donationFilters.dateRangeEnd = ''
      }
    }

    const exportDonations = () => {
      setFilters()
      const { ordering, ...filters } = donationFilters
      DonationApi.csc.generateExport({
        ...filters,
        polygon: exportPolygon.value,
      })
    }
    const getSortIcon = (column) => {
      const order = getSortOrder(column)
      if (order === 'asc') return 'sort-up'
      if (order === 'desc') return 'sort-down'
      return 'sort'
    }

    //Customize Template

    const showCustomizeModal = ref(false)
    const showSaveTemplateModal = ref(false)
    const templateName = ref('')
    const autoExportEnabled = ref(false)
    const scheduleFrequency = ref('daily')
    const scheduleWeekday = ref(0)
    const scheduleStartDate = ref('')
    const scheduleStartDateConfig = {
      minDate: 'today',
      dateFormat: 'Y-m-d',
    }
    const weekdayOptions = [
      { value: 0, label: 'Monday' },
      { value: 1, label: 'Tuesday' },
      { value: 2, label: 'Wednesday' },
      { value: 3, label: 'Thursday' },
      { value: 4, label: 'Friday' },
      { value: 5, label: 'Saturday' },
      { value: 6, label: 'Sunday' },
    ]
    const canSaveExportTemplate = computed(() => {
      if (!templateName.value.trim()) return false
      if (!autoExportEnabled.value) return true
      if (!scheduleStartDate.value) return false
      // Disallow past start dates (calendar also uses minDate: today)
      const start = new Date(`${scheduleStartDate.value}T00:00:00`)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (Number.isNaN(start.getTime()) || start < today) return false
      if (scheduleFrequency.value === 'weekly' && scheduleWeekday.value === null) return false
      return true
    })

    const resetSaveTemplateForm = () => {
      templateName.value = ''
      autoExportEnabled.value = false
      scheduleFrequency.value = 'daily'
      scheduleWeekday.value = 0
      scheduleStartDate.value = ''
    }

    const openSaveTemplateModal = () => {
      resetSaveTemplateForm()
      showSaveTemplateModal.value = true
    }

    const getTemplateScheduleLabel = (template) => {
      const enabled = template?.autoExportEnabled ?? template?.auto_export_enabled
      if (!enabled) return ''
      const frequency = template?.scheduleFrequency ?? template?.schedule_frequency
      const weekday = template?.scheduleWeekday ?? template?.schedule_weekday
      if (frequency === 'weekly') {
        const day = weekdayOptions.find((d) => d.value === weekday)
        return day ? `Weekly · ${day.label}` : 'Weekly'
      }
      if (frequency === 'daily') return 'Daily'
      return 'Automated'
    }
    const exportFields = ref({
      givarooId: { label: 'Pledge ID', selected: false },
      npoName: { label: 'Campaign', selected: false },
      donorFullName: { label: 'Donor Name', selected: false },
      acquisitionPlatform: { label: 'Acquisition Platform', selected: false },
      totalWeeks: { label: 'Reporting Week', selected: false },
      pledgeType: { label: 'Pledge type', selected: false },
      pledgeDate: { label: 'Pledge Date', selected: false },
      fundraiserFullName: { label: 'Fundraiser', selected: false },
      location: { label: 'Market', selected: false },
      canvassingOrgName: { label: 'Vendor', selected: false },
      frequencyName: { label: 'Frequency', selected: false },
      amount: { label: 'Amount', selected: false },
      userAge: { label: 'Age', selected: false },
      donorAddressStreet: { label: 'Donor Address', selected: false },
      donorCity: { label: 'Donor City', selected: false },
      donorState: { label: 'Donor State', selected: false },
      donorPostalCode: { label: 'Donor Zip', selected: false },
      dayOfWeek: { label: 'Day of Week', selected: false },
      timeAtSignup: { label: 'Time at signup', selected: false },
      teamTimeZone: { label: 'Team Timezone', selected: false },
      donorEmail: { label: 'Email', selected: false },
      emailDeliveryStatus: { label: 'Email Delivery Status', selected: false },
      donorMobilePhone: { label: 'Phone', selected: false },
      smsDeliveryStatus: { label: 'SMS Delivery Status', selected: false },
      confirmationCode: { label: 'Canvass ID', selected: false },
      cardHolderName: { label: 'Card Holder Account Name', selected: false },
      qrCode: { label: 'Form Type', selected: false },
      cardMaskedPan: { label: 'Masked Value', selected: false },
      latitudeLongitude: { label: 'Latitude Longitude', selected: false },
      paymentMethod: { label: 'Payment Method', selected: false },
      premiumPledge: { label: 'Premium Pledge?', selected: false },
      status: { label: 'Validation Status', selected: false },
      homeOffice: { label: 'Home Office', selected: false },
      fundraiserEmployeeId: { label: 'Employee Id', selected: false },
      emailOptIn: { label: 'Email Opt In', selected: false },
      smsOptIn: { label: 'SMS Opt In', selected: false },
      dvSmsSent: { label: 'DV SMS Sent', selected: false },
      dvSmsVerified: { label: 'DV SMS Verified', selected: false },
      transactionErrorMsg: { label: 'Transaction Error Message', selected: false },
      currentStatus: { label: 'Current Status', selected: false },
      paymentsBeforeCancellation: { label: 'Payments Before Cancellation', selected: false },
      cardExpirationDate: { label: 'Card Expiration Date', selected: false },
    })
    const selectedFieldsOrder = ref([])
    const toggleExportField = (fieldKey) => {
      exportFields.value[fieldKey].selected = !exportFields.value[fieldKey].selected

      if (exportFields.value[fieldKey].selected) {
        // Add to selection order if selected
        selectedFieldsOrder.value.push(fieldKey)
      } else {
        // Remove from selection order if deselected
        selectedFieldsOrder.value = selectedFieldsOrder.value.filter((key) => key !== fieldKey)
      }
    }

    const clearExportSelection = () => {
      // Clear all selected fields
      Object.keys(exportFields.value).forEach((key) => {
        exportFields.value[key].selected = false
      })
      // Clear the selection order
      selectedFieldsOrder.value = []
    }
    const exportCustomTemplate = async () => {
      try {
        setFilters()
        const { ordering, ...filters } = donationFilters

        // Use the ordered selection instead of filtering all fields
        const selectedFields = selectedFieldsOrder.value

        // Call API with selected fields in order
        await DonationApi.csc.exportCustomizeTemplate({
          fields: selectedFields,
          ...filters,
          polygon: exportPolygon.value,
        })
        showCustomizeModal.value = false
      } catch (error) {
        alert(error)
      }
    }

    const saveTemplate = async () => {
      try {
        if (!canSaveExportTemplate.value) return
        setFilters()
        const { ordering, ...filters } = donationFilters

        await DonationApi.csc.saveExportTemplate({
          name: templateName.value.trim(),
          filterType: 'DonationFilter',
          fields: selectedFieldsOrder.value,
          ...filters,
          autoExportEnabled: autoExportEnabled.value,
          scheduleFrequency: autoExportEnabled.value ? scheduleFrequency.value : null,
          scheduleWeekday:
            autoExportEnabled.value && scheduleFrequency.value === 'weekly'
              ? scheduleWeekday.value
              : null,
          scheduleStartDate: autoExportEnabled.value ? scheduleStartDate.value || null : null,
        })

        // Reset and close
        resetSaveTemplateForm()
        showSaveTemplateModal.value = false
        showCustomizeModal.value = false
        toast.success('Template saved successfully!')
      } catch (error) {
        alert(error)
      }
    }

    const getSavedTemplateFilterType = (t) => {
      const raw = t?.type ?? t?.filterType ?? t?.template?.filter_type ?? t?.template?.filterType
      return typeof raw === 'string' ? raw.trim() : raw
    }

    const fetchTemplates = async () => {
      try {
        loadingTemplates.value = true
        const response = await DonationApi.csc.getExportTemplate()
        const all = Array.isArray(response) ? response : response?.results ?? []
        savedTemplates.value = all.filter((t) => {
          const ft = getSavedTemplateFilterType(t)
          return (
            ft !== FUNDRAISER_STATS_BY_DAY_FILTER_TYPE &&
            ft !== CAMPAIGN_DATA_FUNDRAISER_FILTER_TYPE
          )
        })
      } catch (error) {
        alert(error)
      } finally {
        loadingTemplates.value = false
      }
    }

    const removeTemplate = async (templateId) => {
      try {
        await DonationApi.csc.removeSavedFilterTemplate({ templateId })
        toast.success('Template removed successfully!')
        // Refresh the templates list
        await fetchTemplates()
      } catch (error) {
        toast.error('Failed to remove template')
        console.error('Error removing template:', error)
      }
    }

    const applyTemplate = (template) => {
      // Clear current selections
      clearExportSelection()
      selectedCampaignFilter.value = []
      selectedFundraiserFilter.value = []
      selectedFrequencyFilter.value = []
      selectedStatusFilter.value = []
      selectedDonorStatusFilter.value = []
      selectedDateRange.date1 = ''
      selectedDateRange.date2 = ''

      // Apply fields from template
      if (template.template.fields) {
        template.template.fields.forEach((field) => {
          if (exportFields.value[field]) {
            exportFields.value[field].selected = true
            selectedFieldsOrder.value.push(field)
          }
        })
      }

      // Apply filters from template
      if (template.template.campaign) {
        const campaignIds = template.template.campaign.split(',')
        selectedCampaignFilter.value = campaignFilters.value.filter((campaign) =>
          campaignIds.includes(campaign.id),
        )
      }

      if (template.template.fundraiser) {
        const fundraiserIds = template.template.fundraiser.split(',')
        selectedFundraiserFilter.value = fundraiserFilters.value.filter((fundraiser) =>
          fundraiserIds.includes(fundraiser.id),
        )
      }

      if (template.template.frequency) {
        const frequencyIds = template.template.frequency.split(',')
        selectedFrequencyFilter.value = frequencyFilters.value.filter((frequency) =>
          frequencyIds.includes(frequency.id),
        )
      }

      if (template.template.status) {
        const statusIds = template.template.status.split(',')
        selectedStatusFilter.value = statusFilters.value.filter((status) =>
          statusIds.includes(status.id),
        )
      }

      if (template.template.current_donor_status) {
        const donorStatusIds = template.template.current_donor_status.split(',')
        selectedDonorStatusFilter.value = donorStatusFilters.value.filter((status) =>
          donorStatusIds.includes(status.id),
        )
      }

      // Close the modal
      showTemplatesModal.value = false
      showCustomizeModal.value = true
    }

    watch(
      [
        () => selectedCampaignFilter.value,
        () => selectedFundraiserFilter.value,
        () => selectedFrequencyFilter.value,
        () => selectedStatusFilter.value,
        () => selectedDonorStatusFilter.value,
        () => selectedDonorFilter.value,
        () => selectedGivarooIdFilter.value,
      ],
      () => {
        setFilters()
        getDonations()
      },
    )

    // Second watch: Handles template modal visibility
    watch(showTemplatesModal, (newValue) => {
      if (newValue) {
        fetchTemplates()
      }
    })

    return {
      donations,
      donationRefs,
      donationFilters,
      isGivarooStaff,
      isNPOAdmin,
      isCanvassingOrgAdmin,
      isCanvassingOrgFieldDirector,
      isFundraiser,
      canViewDonationMap,
      showMapView,
      toggleMapView,
      loading,
      updatingTable,
      showFilterDrawer,
      canShowFilters,
      activeFilterCount,
      tableColumns,
      notesButtonClass,
      statusBadgeClass,
      FunnelIcon,
      ArrowDownTrayIcon,
      MapIcon,
      Cog6ToothIcon,
      DocumentDuplicateIcon,
      ArrowPathIcon,
      campaignFilters,
      fundraiserFilters,
      frequencyFilters,
      statusFilters,
      donorStatusFilters,
      donorFilters,
      selectedCampaignFilter,
      selectedFundraiserFilter,
      selectedFrequencyFilter,
      selectedStatusFilter,
      selectedDonorStatusFilter,
      selectedDonorFilter,
      selectedDateRange,
      formatDateString,
      formatCurrency,
      formatToTitleCase,
      getStatus,
      resubmitDonation,
      resubmittingDonationId,
      showNotesModal,
      notesDraft,
      savingNotes,
      openNotesModal,
      closeNotesModal,
      saveNotes,
      exportDonations,
      nextPage,
      prevPage,
      updateItemsPerPage,
      itemsPerPage,
      totalPages,
      customLabel,
      sortData,
      clearSorting,
      getSortOrder,
      getSortIcon,
      faSort,
      faSortDown,
      faSortUp,
      sortColumnOptions,
      displayStart,
      displayEnd,
      handleDateChange,
      exportCustomTemplate,
      toggleExportField,
      showCustomizeModal,
      exportFields,
      clearExportSelection,
      showSaveTemplateModal,
      templateName,
      autoExportEnabled,
      scheduleFrequency,
      scheduleWeekday,
      scheduleStartDate,
      scheduleStartDateConfig,
      weekdayOptions,
      canSaveExportTemplate,
      getTemplateScheduleLabel,
      resetSaveTemplateForm,
      openSaveTemplateModal,
      saveTemplate,
      selectedFieldsOrder,
      showTemplatesModal,
      savedTemplates,
      loadingTemplates,
      fetchTemplates,
      applyTemplate,
      removeTemplate,
      donorCustomLabel,
      searchDonors,
      isSearchingDonors,
      handleDonorSearch,
      handleFundraiserSearch,
      searchFundraisers,
      isSearchingFundraisers,
      selectedGivarooIdFilter,
      givarooIdFilters,
      isSearchingGivarooIds,
      handleGivarooIdSearch,
      searchGivarooIds,
      canExport,
      mapLocations,
      mapLoading,
      mapUpdating,
      exportPolygon,
      onMapPolygonChange,
    }
  },
}
</script>

<style>
.donation-actions-toolbar {
  margin-bottom: 1rem;
}

.donation-actions-toolbar .btn--table--export {
  margin-top: 0;
  margin-bottom: 0;
}

@media (max-width: 639px) {
  .donation-actions-toolbar .btn--table--export {
    flex: 1 1 100%;
    width: 100%;
  }

  .donation-actions-toolbar > .bg-white {
    flex: 1 1 100%;
    width: 100%;
    text-align: center;
  }
}

@media (min-width: 640px) and (max-width: 900px) {
  .donation-actions-toolbar .btn--table--export,
  .donation-actions-toolbar > .bg-white {
    flex: 0 1 calc(50% - 0.25rem);
    min-width: 0;
    max-width: calc(50% - 0.25rem);
  }

  .donation-actions-toolbar > .bg-white {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
}

.btn--table--export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
